import type { ChatMessage, Conversation, WorkspaceMode } from '@auw/workspace-utils';
import i18n from '@auw/i18n';

export interface GroupedSidebarEntries {
  label: string;
  ids: string[];
}

export interface SidebarTreeShape {
  roots: string[];
  childrenByParent: Record<string, string[]>;
}

const DAY_MS = 86_400_000;
const messageRolePresenceCache = new WeakMap<ChatMessage[], boolean>();

type DateBucket = 'today' | 'yesterday' | 'last7' | 'last30' | 'older';

const DATE_BUCKET_ORDER: DateBucket[] = ['today', 'yesterday', 'last7', 'last30', 'older'];

function dateBucketLabel(bucket: DateBucket): string {
  switch (bucket) {
    case 'today':
      return i18n.t('common:time.today');
    case 'yesterday':
      return i18n.t('common:time.yesterday');
    case 'last7':
      return i18n.t('common:time.last7days');
    case 'last30':
      return i18n.t('common:time.last30days');
    case 'older':
      return i18n.t('common:time.older');
    default: {
      const _exhaustive: never = bucket;
      return _exhaustive;
    }
  }
}

function emptyDateGroups(): Record<DateBucket, string[]> {
  return {
    today: [],
    yesterday: [],
    last7: [],
    last30: [],
    older: [],
  };
}

export function getPageLabel(_mode: WorkspaceMode): string {
  return i18n.t('common:page.newPage');
}

export function getSubPageLabel(_mode: WorkspaceMode): string {
  return i18n.t('common:page.createSubPage');
}

export function getPageDeleteLabel(_mode: WorkspaceMode): string {
  return i18n.t('common:page.deletePage');
}

export function extractWorkspaceHttpStatus(err: unknown): number | null {
  if (!(err instanceof Error)) return null;
  const matched = err.message.match(/HTTP\s+(\d+)/i);
  if (!matched) return null;
  const code = Number(matched[1]);
  return Number.isFinite(code) ? code : null;
}

export function groupConversationsByDate(
  conversations: Record<string, Conversation>,
  order: string[],
): GroupedSidebarEntries[] {
  const now = Date.now();
  const pinned: string[] = [];
  const groups = emptyDateGroups();

  for (const id of order) {
    const conversation = conversations[id];
    if (!conversation) continue;
    if (conversation.pinnedAt) {
      pinned.push(id);
      continue;
    }
    const diff = now - conversation.updatedAt;
    if (diff < DAY_MS) groups.today.push(id);
    else if (diff < 2 * DAY_MS) groups.yesterday.push(id);
    else if (diff < 7 * DAY_MS) groups.last7.push(id);
    else if (diff < 30 * DAY_MS) groups.last30.push(id);
    else groups.older.push(id);
  }

  return [
    ...(pinned.length > 0 ? [{ label: i18n.t('common:page.pinned'), ids: pinned }] : []),
    ...DATE_BUCKET_ORDER
      .filter((key) => groups[key].length > 0)
      .map((key) => ({ label: dateBucketLabel(key), ids: groups[key] })),
  ];
}

export function groupSidebarEntriesByDate<T extends { createdAt: number; pinnedAt: string | null }>(
  entries: Record<string, T>,
  order: string[],
): GroupedSidebarEntries[] {
  const now = Date.now();
  const pinned: string[] = [];
  const groups = emptyDateGroups();

  for (const id of order) {
    const entry = entries[id];
    if (!entry) continue;
    if (entry.pinnedAt) {
      pinned.push(id);
      continue;
    }
    const diff = now - entry.createdAt;
    if (diff < DAY_MS) groups.today.push(id);
    else if (diff < 2 * DAY_MS) groups.yesterday.push(id);
    else if (diff < 7 * DAY_MS) groups.last7.push(id);
    else if (diff < 30 * DAY_MS) groups.last30.push(id);
    else groups.older.push(id);
  }

  return [
    ...(pinned.length > 0 ? [{ label: i18n.t('common:page.pinned'), ids: pinned }] : []),
    ...DATE_BUCKET_ORDER
      .filter((key) => groups[key].length > 0)
      .map((key) => ({ label: dateBucketLabel(key), ids: groups[key] })),
  ];
}

export function hasChatMessages(conversation: Conversation): boolean {
  const messages = conversation.messages;
  const cached = messageRolePresenceCache.get(messages);
  if (cached !== undefined) return cached;
  const hasChat = messages.some((message) => message.role === 'user' || message.role === 'assistant');
  messageRolePresenceCache.set(messages, hasChat);
  return hasChat;
}

export function buildSidebarTree(
  items: Conversation[],
  rankMap: Map<string, number>,
): SidebarTreeShape {
  const idSet = new Set(items.map((item) => item.id));
  const childrenByParent: Record<string, string[]> = { root: [] };
  const byId = new Map(items.map((item) => [item.id, item]));

  for (const item of items) {
    const rawParent = item.workspaceParentId;
    const parent = rawParent && idSet.has(rawParent) ? rawParent : null;
    const key = parent ?? 'root';
    if (!childrenByParent[key]) childrenByParent[key] = [];
    childrenByParent[key].push(item.id);
  }

  const sorter = (a: string, b: string) => {
    const conversationA = byId.get(a);
    const conversationB = byId.get(b);
    const kindRankA = conversationA?.workspaceKind === 'folder' ? 0 : 1;
    const kindRankB = conversationB?.workspaceKind === 'folder' ? 0 : 1;
    if (kindRankA !== kindRankB) return kindRankA - kindRankB;

    const rankA = rankMap.get(a) ?? Number.MAX_SAFE_INTEGER;
    const rankB = rankMap.get(b) ?? Number.MAX_SAFE_INTEGER;
    if (rankA !== rankB) return rankA - rankB;
    if (!conversationA || !conversationB) return 0;
    return conversationB.updatedAt - conversationA.updatedAt;
  };

  Object.keys(childrenByParent).forEach((key) => {
    childrenByParent[key].sort(sorter);
  });

  return {
    roots: childrenByParent.root ?? [],
    childrenByParent,
  };
}
